<template>
  <div class="ratings-to-client-tab">
    <div class="card-header py-3 px-0">
      <div class="card-title flex-column" style="justify-content: space-evenly">
        <h3 class="card-label font-weight-bolder text-dark m-0">
          Ratings to Client
        </h3>
      </div>
    </div>
    <div class="table-responsive mb-0">
      <table
        class="table table-head-custom table-vertical-center table-head-bg table-borderless"
      >
        <thead>
          <tr class="text-left">
            <th>Talent Name</th>
            <th>Role</th>
            <th>Rating Date</th>
            <th>Rating Score</th>
            <th>Selected Criteria</th>
            <th>Review</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedClientRatings" :key="item.id">
            <td>
              <router-link :to="`/dashboard/talent/${item.talentId}`">
                {{ item.talentName }}
              </router-link>
            </td>
            <td>{{ item.role }}</td>
            <td>{{ item.ratingDate }}</td>
            <td>
              <v-rating
                v-model="item.rating"
                readonly
                dense
                small
                color="amber"
                length="5"
                empty-icon="mdi-star"
                full-icon="mdi-star"
                background-color="#E0E0E0"
              ></v-rating>
            </td>
            <td>{{ item.criteria.join(", ") }}</td>
            <td>
              <v-btn
                depressed
                color="primary"
                class="mr-2"
                @click="viewReview(item)"
              >
                View Talent's review
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Pagination -->
    <div class="pagination-container">
      <p
        v-if="dummyClientRatings.length > 0"
        class="pagination-container__desc"
      >
        {{
          $t("showing_results", {
            length: paginatedClientRatings.length,
            total: dummyClientRatings.length,
          })
        }}
      </p>
      <b-pagination
        v-if="totalPages > 1"
        v-model="currentPage"
        :total-rows="dummyClientRatings.length"
        :per-page="perPage"
        align="center"
        last-number
        first-number
      ></b-pagination>
    </div>

    <!-- Review Modal -->
    <v-dialog v-model="reviewDialog" max-width="600px">
      <v-card>
        <v-card-title>Review Details</v-card-title>
        <v-card-text>
          <div v-if="selectedReview">
            <div class="mb-4">
              <strong>Rating:</strong>
              <star-rating-with-labels
                :value="selectedReview.rating"
                :interactive="false"
                :disable-hover="true"
                :star-size="48"
                :labels="['worst', 'Bad', 'Neutral', 'Good', 'Excellent']"
                :active-color="'#FFC107'"
                :inactive-color="'#E0E0E0'"
                class="ma-2"
              />
            </div>
            <div class="mb-4">
              <strong>Criteria:</strong>
              <div>{{ selectedReview.criteria.join(", ") }}</div>
            </div>
            <div>
              <strong>Review:</strong>
              <p>{{ selectedReview.review }}</p>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="reviewDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import StarRatingWithLabels from '@/components/shared/StarRatingWithLabels.vue';

export default {
  name: "RatingsToClientTab",
  components: {
    StarRatingWithLabels,
  },
  props: {
    selectedProject: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      reviewDialog: false,
      selectedReview: null,
      currentPage: 1,
      perPage: 8,
      dummyClientRatings: [
        {
          id: 1,
          talentId: 101,
          talentName: "John Doe",
          role: "Senior Developer",
          ratingDate: "2024-03-15",
          rating: 3,
          criteria: ["Technical Skills", "Communication", "Problem Solving"],
          review:
            "Excellent technical skills and great communication. Very responsive to feedback and always delivers high-quality work.",
        },
        {
          id: 2,
          talentId: 102,
          talentName: "Jane Smith",
          role: "UI/UX Designer",
          ratingDate: "2024-03-14",
          rating: 5,
          criteria: ["Design Skills", "Creativity", "Team Collaboration"],
          review:
            "Outstanding design work and very creative solutions. Great team player and always meets deadlines.",
        },
        {
          id: 3,
          talentId: 103,
          talentName: "Mike Johnson",
          role: "Backend Developer",
          ratingDate: "2024-03-13",
          rating: 4,
          criteria: ["Code Quality", "Performance", "Documentation"],
          review:
            "Solid backend development skills. Good code quality and documentation. Could improve on performance optimization.",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      clientRatings: "ratings/getClientRatings",
    }),
    totalPages() {
      return Math.ceil(this.dummyClientRatings.length / this.perPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    endIndex() {
      return Math.min(
        this.startIndex + this.perPage,
        this.dummyClientRatings.length
      );
    },
    paginatedClientRatings() {
      return this.dummyClientRatings.slice(this.startIndex, this.endIndex);
    },
  },
  watch: {
    selectedProject: {
      handler(newProject, oldProject) {
        // Only refetch if project actually changed and we have a valid project
        if (newProject && newProject !== oldProject) {
          this.fetchData();
        }
      },
      immediate: false,
    },
  },
  methods: {
    ...mapActions({
      fetchClientRatings: "ratings/fetchClientRatings",
    }),
    viewReview(item) {
      this.selectedReview = item;
      this.reviewDialog = true;
    },
    async fetchData() {
      await this.fetchClientRatings(this.clientId);
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.ratings-to-client-tab {
  padding: 1rem;
}

.table-responsive {
  margin-bottom: 0;
}

.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #181c32;
  border-collapse: collapse;
}

.table th {
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.1rem;
  padding: 0.75rem 1rem;
  background-color: #f3f6f9;
  border-bottom: 1px solid #ebedf3;
}

.table td {
  padding: 0.75rem 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #ebedf3;
}

.table-borderless td,
.table-borderless th {
  border: 0;
}

.table-head-bg th {
  background-color: #f3f6f9;
}

.table-vertical-center td {
  vertical-align: middle;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  flex-wrap: wrap;
  min-height: 70px;
  padding: 0 2.25rem;
  background-color: transparent;
  border-bottom: 1px solid #ebedf3;
}

.card-title {
  display: flex;
  margin: 0.5rem;
  margin-left: 0;
  flex-wrap: wrap;
  align-items: center;
}

.card-label {
  display: flex;
  align-items: center;
  margin: 0;
  margin-left: 0.75rem;
  margin-right: 0.5rem;
  font-weight: 500;
  color: #181c32;
  font-size: 1.275rem;
}

.pagination-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1rem;
}

.pagination-container__desc {
  margin-bottom: 0.5rem;
  color: #7e8299;
  font-size: 0.9rem;
}

.star-rating-with-labels {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.star-labels {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 180px;
  margin-top: 0.2rem;
}
.star-labels span {
  flex: 1;
  text-align: center;
  color: #3d2586;
  font-weight: bold;
  font-size: 0.85rem;
  text-transform: capitalize;
}
</style>
